import * as S from './styled'

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMediaQuery } from 'react-responsive'
import { t } from 'i18next'

import logoGold from '@/assets/image/logo_gold.png'
import logoGoldM from '@/assets/image/logo_gold_m.png'
import GoldLang from '@/components/gold/GoldLang'

// 그랜드라인(build:jp 등): v5 기본로고 사용
const BRAND_LOGO = logoGold
const BRAND_LOGO_M = logoGoldM

const PopupContext = createContext(null)
const ConfirmContext = createContext(null)
const AlertContext = createContext(null)

/**
 * 팝업 열기/닫기 훅
 * @returns {{ openPopup: function, closePopup: function }}
 */
export function usePopup() {
  const ctx = useContext(PopupContext)
  if (!ctx) throw new Error('usePopup은 PopupProvider 안에서 사용해야 합니다')
  return { openPopup: ctx.openPopup, closePopup: ctx.closePopup }
}

/**
 * 확인창 열기 훅
 * @returns {{ openConfirm: function }}
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm은 PopupProvider 안에서 사용해야 합니다')
  return { openConfirm: ctx.openConfirm }
}

/**
 * 알림창 열기 훅
 * @returns {{ openAlert: function }}
 */
export function useAlert() {
  const ctx = useContext(AlertContext)
  if (!ctx) throw new Error('useAlert은 PopupProvider 안에서 사용해야 합니다')
  return { openAlert: ctx.openAlert }
}

/** 모바일 화면 기준 — Routers 와 같은 850px */
const useIsMobile = () => useMediaQuery({ maxWidth: 850 })

/** 알림 확인 버튼 문구 — gold PC 「もちろん」, 모바일 「確定」 */
const okLabel = (mobile) => (mobile ? t('gold.alertOkM') : t('gold.alertOk'))

/**
 * ver2(gold) 알림 상자 — 일반 팝업·알림창·확인창 공통(gold8.bet .alertDialog 모양).
 *   제목이 없으면 gold 처럼 「ヒント」. 버튼은 오른쪽 아래(보조 버튼은 흰 바탕).
 * @param {{ title?: React.ReactNode, children: React.ReactNode, buttons: Array<{label: string, onClick: function, sub?: boolean}>, onOverlayClick?: function }} props
 */
function GoldDialog({ title, children, buttons, onOverlayClick }) {
  return createPortal(
    <S.GoldAlertOverlay onClick={onOverlayClick}>
      <S.GoldAlertBox role="dialog" aria-modal="true">
        <S.GoldAlertHeader>{title || t('gold.hint')}</S.GoldAlertHeader>
        <S.GoldAlertBody>{children}</S.GoldAlertBody>
        <S.GoldAlertFooter>
          {buttons.map((b, i) => (
            <S.GoldAlertBtn key={i} $sub={b.sub} onClick={b.onClick}>{b.label}</S.GoldAlertBtn>
          ))}
        </S.GoldAlertFooter>
      </S.GoldAlertBox>
    </S.GoldAlertOverlay>,
    document.body,
  )
}

/**
 * 팝업 오버레이 + 모달을 렌더링하는 내부 컴포넌트
 * @param {Object} popup - 팝업 설정 객체
 * @param {function} onClose - 닫기 콜백
 */
function PopupRenderer({ popup, onClose }) {
  const isMobile = useIsMobile()

  useEffect(() => {
    const prev = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    return () => {
      document.documentElement.style.overflow = prev
      document.body.style.overflow = prev
    }
  }, [])

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [onClose])

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose()
  }

  // raw: 내용이 자기 바깥 막·상자를 직접 그리는 팝업(예: gold 회원가입 창) — 틀 없이 그대로 띄운다
  if (popup.raw) return createPortal(popup.content, document.body)

  // ver2(gold) 로그인 창 — 전역 .modal 규칙과 섞이지 않는 전용 틀(gold8.bet PC 로그인 창 / 모바일 로그인 화면 모양)
  if (popup.grandLogin) {
    const mobile = !!popup.content?.props?.isMobile
    // 모바일: m.gold8.bet /Login 처럼 화면 전체(위쪽 룰렛 사진 배경) + 왼쪽 위 로고 + 오른쪽 위 닫기 + 검은 카드 + 아래 언어 상자
    if (mobile) {
      return createPortal(
        <S.GoldMLoginPage role="dialog" aria-modal="true">
          <S.GoldMLoginLogo src={BRAND_LOGO_M} alt="" />
          <S.GoldMLoginClose type="button" onClick={onClose} aria-label="close">✕</S.GoldMLoginClose>
          <S.GoldLoginBox $mobile>
            <S.GoldLoginTitle $mobile>{t('header.login')}</S.GoldLoginTitle>
            {popup.content}
          </S.GoldLoginBox>
          <S.GoldMLoginLang><GoldLang mobile /></S.GoldMLoginLang>
        </S.GoldMLoginPage>,
        document.body,
      )
    }
    return createPortal(
      <S.GoldLoginOverlay $mobile={mobile} onClick={handleOverlayClick}>
        <S.GoldLoginBox $mobile={mobile} role="dialog" aria-modal="true">
          <S.GoldLoginClose type="button" $mobile={mobile} onClick={onClose} aria-label="close">✕</S.GoldLoginClose>
          {!mobile && <S.GoldLoginLogo src={BRAND_LOGO} alt="" />}
          <S.GoldLoginTitle $mobile={mobile}>{mobile ? t('header.login') : t('gold.signInTitle')}</S.GoldLoginTitle>
          {popup.content}
        </S.GoldLoginBox>
      </S.GoldLoginOverlay>,
      document.body,
    )
  }

  // 일반 팝업(제목 + 내용) — gold 알림 상자 + 확인 버튼. 바깥·Esc 로도 닫힌다.
  return (
    <GoldDialog
      title={popup.title}
      onOverlayClick={handleOverlayClick}
      buttons={[{ label: okLabel(isMobile), onClick: onClose }]}
    >
      {popup.content}
    </GoldDialog>
  )
}

/**
 * 확인창 렌더러 — gold 알림 상자 + [취소][확정]
 * @param {Object} confirm - { title, message, mainBtn, subBtn, Func }
 * @param {function} onClose - 닫기 콜백
 */
function ConfirmRenderer({ confirm, onClose }) {
  useEffect(() => {
    document.body.classList.add('scroll-lock')
    return () => document.body.classList.remove('scroll-lock')
  }, [])

  const handleConfirm = () => {
    onClose()
    if (confirm.Func) confirm.Func()
  }

  return (
    <GoldDialog
      title={confirm.title}
      buttons={[
        { label: confirm.subBtn || t('gold.cancel'), sub: true, onClick: onClose },
        { label: confirm.mainBtn || t('gold.alertOkM'), onClick: handleConfirm },
      ]}
    >
      {confirm.message}
    </GoldDialog>
  )
}

/**
 * 알림창 렌더러 (확인 버튼만 있는 단일 버튼 대화상자) — gold 알림 상자
 * @param {Object} alert - { title, message, message2, mainBtn, Func }
 * @param {function} onClose - 닫기 콜백
 */
function AlertRenderer({ alert, onClose }) {
  const isMobile = useIsMobile()

  useEffect(() => {
    document.body.classList.add('scroll-lock')
    return () => document.body.classList.remove('scroll-lock')
  }, [])

  const handleClose = () => {
    onClose()
    if (alert.Func) alert.Func()
  }

  return (
    <GoldDialog
      title={alert.title}
      buttons={[{ label: alert.mainBtn || okLabel(isMobile), onClick: handleClose }]}
    >
      {alert.message}
      {alert.message2 && <><br />{alert.message2}</>}
    </GoldDialog>
  )
}

/**
 * 팝업 + 확인창 + 알림창 상태 관리 Provider
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export function PopupProvider({ children }) {
  const [popup, setPopup] = useState(null)
  const [confirm, setConfirm] = useState(null)
  const [alert, setAlert] = useState(null)

  const openPopup = useCallback((config) => {
    setPopup(config)
  }, [])

  const closePopup = useCallback(() => {
    setPopup(null)
  }, [])

  const openConfirm = useCallback((config) => {
    setConfirm(config)
  }, [])

  const closeConfirm = useCallback(() => {
    setConfirm(null)
  }, [])

  const openAlert = useCallback((config) => {
    setAlert(config)
  }, [])

  const closeAlert = useCallback(() => {
    setAlert(null)
  }, [])

  // api.js 등 React 외부에서 openAlert 호출 가능하도록 전역 이벤트 브리지
  useEffect(() => {
    const handler = (e) => openAlert(e.detail || {})
    window.addEventListener('app:alert', handler)
    return () => window.removeEventListener('app:alert', handler)
  }, [openAlert])

  return (
    <AlertContext.Provider value={{ openAlert }}>
      <ConfirmContext.Provider value={{ openConfirm }}>
        <PopupContext.Provider value={{ openPopup, closePopup }}>
          {children}
          {popup && <PopupRenderer popup={popup} onClose={closePopup} />}
          {confirm && <ConfirmRenderer confirm={confirm} onClose={closeConfirm} />}
          {alert && <AlertRenderer alert={alert} onClose={closeAlert} />}
        </PopupContext.Provider>
      </ConfirmContext.Provider>
    </AlertContext.Provider>
  )
}

/** 기존 코드 하위 호환용 alias */
export const WindowProvider = PopupProvider
